import React, { useRef, useState, useEffect, KeyboardEvent, ClipboardEvent } from 'react';
import { Box } from '@mui/material';
import './index.css';

interface InputOTPProps {
    /** Number of OTP digits (default: 6) */
    length?: number;
    /** Current OTP value */
    value: string;
    /** Callback fired when OTP value changes */
    onChange: (value: string) => void;
    /** If true, the input will be disabled */
    disabled?: boolean;
    /** Callback fired when all digits are filled */
    onComplete?: (value: string) => void;
}

export const InputOTP: React.FC<InputOTPProps> = ({
    length = 6,
    value,
    onChange,
    disabled = false,
    onComplete,
}) => {
    const [focusedIndex, setFocusedIndex] = useState<number>(0);
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    // Initialize input refs array
    useEffect(() => {
        inputRefs.current = inputRefs.current.slice(0, length);
    }, [length]);

    // Auto-focus first empty box on mount
    useEffect(() => {
        if (!disabled && value.length < length) {
            const nextEmptyIndex = value.length;
            inputRefs.current[nextEmptyIndex]?.focus();
            setFocusedIndex(nextEmptyIndex);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Trigger onComplete when all digits are filled
    useEffect(() => {
        if (value.length === length && onComplete) {
            onComplete(value);
        }
    }, [value, length, onComplete]);

    const handleChange = (index: number, digit: string) => {
        if (disabled) return;

        // Only allow single alphanumeric character
        const sanitizedDigit = digit.slice(-1).toUpperCase();
        if (sanitizedDigit && !/^[A-Z0-9]$/.test(sanitizedDigit)) return;

        const newValue = value.split('');
        newValue[index] = sanitizedDigit;
        const updatedValue = newValue.join('').slice(0, length);

        onChange(updatedValue);

        // Move to next input if digit was entered
        if (sanitizedDigit && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
            setFocusedIndex(index + 1);
        }
    };

    const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
        if (disabled) return;

        if (e.key === 'Backspace') {
            e.preventDefault();
            const newValue = value.split('');

            if (newValue[index]) {
                // Clear current box
                newValue[index] = '';
                onChange(newValue.join(''));
            } else if (index > 0) {
                // Move to previous box and clear it
                newValue[index - 1] = '';
                onChange(newValue.join(''));
                inputRefs.current[index - 1]?.focus();
                setFocusedIndex(index - 1);
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
            setFocusedIndex(index - 1);
        } else if (e.key === 'ArrowRight' && index < length - 1) {
            inputRefs.current[index + 1]?.focus();
            setFocusedIndex(index + 1);
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        if (disabled) return;

        const pastedData = e.clipboardData.getData('text/plain').trim().toUpperCase();
        const sanitized = pastedData.replace(/[^A-Z0-9]/g, '').slice(0, length);

        onChange(sanitized);

        // Focus the next empty box or the last box
        const nextIndex = Math.min(sanitized.length, length - 1);
        inputRefs.current[nextIndex]?.focus();
        setFocusedIndex(nextIndex);
    };

    const handleFocus = (index: number) => {
        setFocusedIndex(index);
    };

    const handleClick = (index: number) => {
        if (disabled) return;
        inputRefs.current[index]?.focus();
        setFocusedIndex(index);
    };

    const renderBoxes = (start: number, end: number) => {
        const boxes = [];
        for (let i = start; i < end; i++) {
            const isFirst = i === start;
            const isLast = i === end - 1;
            const isFocused = i === focusedIndex && !disabled;
            const hasValue = value[i] !== undefined && value[i] !== '';

            boxes.push(
                <div
                    key={i}
                    className={`otp-box ${isFirst ? 'first' : ''} ${isLast ? 'last' : ''} ${isFocused ? 'focused' : ''} ${disabled ? 'disabled' : ''}`}
                    onClick={() => handleClick(i)}
                >
                    <input
                        ref={(el) => (inputRefs.current[i] = el)}
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        maxLength={1}
                        value={value[i] || ''}
                        onChange={(e) => handleChange(i, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(i, e)}
                        onFocus={() => handleFocus(i)}
                        onPaste={handlePaste}
                        disabled={disabled}
                        className="otp-input"
                        aria-label={`Digit ${i + 1}`}
                    />
                    {isFocused && !hasValue && <span className="cursor">|</span>}
                </div>
            );
        }
        return boxes;
    };

    const halfLength = Math.ceil(length / 2);

    return (
        <Box className="otp-container">
            <div className="otp-group">
                {renderBoxes(0, halfLength)}
            </div>
            <div className="otp-separator" />
            <div className="otp-group">
                {renderBoxes(halfLength, length)}
            </div>
        </Box>
    );
};

export default InputOTP;
